import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import type { audience } from '@bb/contracts';
import { Button } from '@bb/ui';
import type { AdminApi } from '../../api/client.ts';
import { FilterGroup } from './FilterGroup.tsx';
import { CORE_CATALOG } from './FilterRow.tsx';
import { moveChild, newGroup, nodeId, type FilterNode, type GroupNode } from './tree-edit.ts';
import { UserLookupPanel } from './UserLookupPanel.tsx';

type Definition = audience.AudienceDefinition;

export const emptyAudience = (): Definition => ({ schemaVersion: 1, include: { kind: 'and', id: nodeId('root'), children: [newGroup()] }, exclude: [], apps: { mode: 'all' } });

interface Props {
  environmentId: string;
  value: Definition;
  onChange: (value: Definition) => void;
  readOnly?: boolean;
  locale?: 'uk' | 'en';
  /** Debounce of the server description while typing. */
  debounceMs?: number;
}

const asGroup = (node: FilterNode): GroupNode => (node.kind === 'and' || node.kind === 'or' ? node : { kind: 'and', id: nodeId('g'), children: [node] });

/**
 * Audience builder (BB-40) for segments, campaigns, Canvas and cards: filter groups joined by AND/OR, exclusion groups,
 * app targeting, and the server's sentence with errors and warnings. Controlled: the parent stores `value`.
 */
export function AudienceBuilder({ environmentId, value, onChange, readOnly = false, locale = 'en', debounceMs = 300 }: Props) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const root = asGroup(value.include);
  const [described, setDescribed] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDescribed(value), debounceMs);
    return () => clearTimeout(timer);
  }, [value, debounceMs]);
  const description = useQuery({ queryKey: ['audience-describe', environmentId, locale, described], queryFn: () => api.describeAudience(environmentId, described, locale) });
  const apps = useQuery({ queryKey: ['apps', environmentId], queryFn: () => api.listApps(environmentId), enabled: value.apps.mode === 'specific' });
  const catalogQuery = useQuery({ queryKey: ['filter-catalog', environmentId], queryFn: () => api.filterCatalog(environmentId), staleTime: Infinity });
  const catalog = catalogQuery.data ?? CORE_CATALOG;
  const warnings = description.data?.warnings ?? [];
  const setRoot = (next: GroupNode) => onChange({ ...value, include: next });
  const setExclusion = (index: number, next: FilterNode | null) =>
    onChange({ ...value, exclude: next ? value.exclude.map((x, i) => (i === index ? next : x)) : value.exclude.filter((_, i) => i !== index) });
  const groupTitle = (node: FilterNode, index: number, prefix: string) => (node.kind !== 'filter' && node.kind !== 'not' && node.label ? node.label : `${prefix} ${index + 1}`);

  return (
    <div className="audience-builder">
      <section aria-label="Include users who match">
        <h3>Include users who match</h3>
        {root.children.length > 1 && (
          <div role="radiogroup" aria-label="Between groups">
            {(['and', 'or'] as const).map((kind) => (
              <label key={kind}>
                <input type="radio" name="audience-root-op" checked={root.kind === kind} disabled={readOnly} onChange={() => setRoot({ ...root, kind })} /> {kind === 'and' ? 'All groups (AND)' : 'Any group (OR)'}
              </label>
            ))}
          </div>
        )}
        {root.children.map((child, index) => (
          <FilterGroup
            key={child.id}
            group={asGroup(child)}
            title={groupTitle(child, index, 'Group')}
            readOnly={readOnly}
            catalog={catalog}
            environmentId={environmentId}
            warnings={warnings}
            onChange={(next) => setRoot({ ...root, children: root.children.map((c, i) => (i === index ? next : c)) })}
            onRemove={() => setRoot({ ...root, children: root.children.filter((_, i) => i !== index) })}
            onMove={(delta) => setRoot(moveChild(root, index, delta))}
            canMoveUp={index > 0}
            canMoveDown={index < root.children.length - 1}
          />
        ))}
        {warnings
          .filter((w) => w.nodeId === root.id)
          .map((w) => (
            <p key={w.message} role="status" className="notice notice--warning">
              {w.message}
            </p>
          ))}
        {!readOnly && <Button onClick={() => setRoot({ ...root, children: [...root.children, newGroup()] })}>Add filter group</Button>}
      </section>

      <section aria-label="Exclude users who match">
        <h3>Exclude users who match</h3>
        {value.exclude.length === 0 && <p className="hint">No exclusion groups.</p>}
        {value.exclude.map((node, index) => (
          <FilterGroup
            key={node.id}
            group={asGroup(node)}
            title={groupTitle(node, index, 'Exclusion group')}
            readOnly={readOnly}
            catalog={catalog}
            environmentId={environmentId}
            warnings={warnings}
            onChange={(next) => setExclusion(index, next)}
            onRemove={() => setExclusion(index, null)}
          />
        ))}
        {!readOnly && <Button onClick={() => onChange({ ...value, exclude: [...value.exclude, newGroup()] })}>Add exclusion group</Button>}
      </section>

      <fieldset aria-label="Apps">
        <legend>Apps</legend>
        {(
          [
            ['all', 'Users of all apps and without an app'],
            ['specific', 'Users of specific apps'],
            ['none', 'Users without an app'],
          ] as const
        ).map(([mode, text]) => (
          <label key={mode}>
            <input
              type="radio"
              name="audience-apps"
              checked={value.apps.mode === mode}
              disabled={readOnly}
              onChange={() => onChange({ ...value, apps: mode === 'specific' ? { mode, appInstanceIds: apps.data?.[0] ? [apps.data[0].id] : [] } : { mode } })}
            />{' '}
            {text}
          </label>
        ))}
        {value.apps.mode === 'specific' &&
          (apps.data ?? []).map((app) => {
            const ids = value.apps.mode === 'specific' ? value.apps.appInstanceIds : [];
            return (
              <label key={app.id}>
                <input
                  type="checkbox"
                  checked={ids.includes(app.id)}
                  disabled={readOnly}
                  onChange={(e) => onChange({ ...value, apps: { mode: 'specific', appInstanceIds: e.target.checked ? [...ids, app.id] : ids.filter((id) => id !== app.id) } })}
                />{' '}
                {app.name}
              </label>
            );
          })}
      </fieldset>

      <section aria-label="Audience summary" aria-live="polite">
        <h3>Summary</h3>
        {description.isPending ? (
          <p aria-busy="true">Describing…</p>
        ) : description.isError ? (
          <p role="alert">The summary could not be loaded.</p>
        ) : description.data.valid ? (
          <ul className="audience-sentences">
            {description.data.sentences.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        ) : (
          <div role="alert">
            <p>The audience is not valid yet:</p>
            <ul>
              {description.data.errors.map((e) => (
                <li key={`${e.path}:${e.message}`}>{e.message}</li>
              ))}
            </ul>
          </div>
        )}
      </section>
      <UserLookupPanel environmentId={environmentId} definition={value} locale={locale} />
    </div>
  );
}
